/**
 * Stage 73.20cu / 73.20cv — HR Approvals for ESS-submitted items.
 * ------------------------------------------------------------------
 * Two tabs:
 *   1. Profile Change Requests — approve/reject an employee-submitted
 *      change to any master field. Approve applies the change to the
 *      employee doc immediately.
 *   2. Statutory Forms         — approve/reject submitted Joining /
 *      Form F / Gratuity / Health Insurance forms.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  CheckCircle2, XCircle, Loader2, Download, ClipboardList, KeyRound,
  Eye, X, ChevronDown, ChevronUp, FileText, ExternalLink, Search, CheckSquare, Square, Filter
} from 'lucide-react';
import { AuthenticatedImage, buildImageUrl } from '../components/AuthenticatedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PREVIEW_SCHEMAS = {
  joining: [
    {
      title: "Personal Details",
      fields: [
        { label: "Full Name", key: "full_name" },
        { label: "Father's / Husband's Name", key: "father_or_husband_name" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Gender", key: "gender" },
        { label: "Marital Status", key: "marital_status" },
        { label: "Blood Group", key: "blood_group" },
        { label: "Nationality", key: "nationality" },
        { label: "Personal Email", key: "personal_email" },
        { label: "Mobile", key: "mobile" }
      ]
    },
    {
      title: "Address",
      fields: [
        { label: "Permanent Address", key: "permanent_address" },
        { label: "Current Address", key: "current_address" },
        { label: "City", key: "city" },
        { label: "State", key: "state" },
        { label: "Pincode", key: "pincode" }
      ]
    },
    {
      title: "Emergency Contact",
      fields: [
        { label: "Name", key: "emergency_contact_name" },
        { label: "Relationship", key: "emergency_contact_relationship" },
        { label: "Phone", key: "emergency_contact_number" }
      ]
    },
    {
      title: "Education (Highest)",
      fields: [
        { label: "Qualification", key: "highest_qualification" },
        { label: "Institution", key: "institution" },
        { label: "Year of Passing", key: "year_of_passing" },
        { label: "Specialisation", key: "specialisation" }
      ]
    },
    {
      title: "Previous Employer",
      fields: [
        { label: "Name", key: "prev_employer_name" },
        { label: "Designation", key: "prev_designation" },
        { label: "From", key: "prev_from" },
        { label: "To", key: "prev_to" },
        { label: "Reason for Leaving", key: "prev_reason" }
      ]
    },
    {
      title: "Bank Details",
      fields: [
        { label: "Bank Name", key: "bank_name" },
        { label: "Account Number", key: "account_number" },
        { label: "IFSC", key: "ifsc_code" },
        { label: "Branch", key: "bank_branch" }
      ]
    },
    {
      title: "Statutory IDs",
      fields: [
        { label: "PAN", key: "pan_number" },
        { label: "Aadhaar", key: "aadhaar_number" },
        { label: "UAN (if any)", key: "uan_number" },
        { label: "Previous PF Number", key: "previous_pf_number" }
      ]
    }
  ],
  form_f: [
    {
      title: "Employee Declaration",
      fields: [
        { label: "Full Name", key: "full_name" },
        { label: "Father's / Husband's Name", key: "father_or_husband_name" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Sex", key: "gender" },
        { label: "Marital Status", key: "marital_status" },
        { label: "Permanent Address", key: "permanent_address" }
      ]
    },
    {
      title: "Nomination Info",
      fields: [
        { label: "EPF Account Number", key: "epf_account_number" },
        { label: "EPS (Pension) Nominee Same As Above?", key: "eps_nominee_same" }
      ]
    },
    {
      title: "Nominees",
      repeater: "nominees",
      fields: [
        { label: "Nominee Name", key: "name" },
        { label: "Relationship", key: "relationship" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Share %", key: "percentage" },
        { label: "Address", key: "address" }
      ]
    }
  ],
  gratuity: [
    {
      title: "Employee Declaration",
      fields: [
        { label: "Full Name", key: "full_name" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Date of Joining", key: "date_of_joining" },
        { label: "Marital Status", key: "marital_status" },
        { label: "Permanent Address", key: "permanent_address" }
      ]
    },
    {
      title: "Family Members / Nominees",
      repeater: "nominees",
      fields: [
        { label: "Name", key: "name" },
        { label: "Relationship", key: "relationship" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Share %", key: "percentage" },
        { label: "Address", key: "address" }
      ]
    }
  ],
  health_insurance: [
    {
      title: "Policy Selection",
      fields: [
        { label: "Policy Type", key: "policy_type" },
        { label: "Sum Insured Elected", key: "sum_insured" },
        { label: "Top-Up Opted?", key: "top_up_opted" },
        { label: "Top-Up Amount", key: "top_up_amount" },
        { label: "Parental Cover?", key: "parental_cover" }
      ]
    },
    {
      title: "Insured Member — Self",
      fields: [
        { label: "Full Name", key: "full_name" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Gender", key: "gender" },
        { label: "Pre-existing Conditions", key: "self_pre_existing" }
      ]
    },
    {
      title: "Dependents",
      repeater: "nominees",
      fields: [
        { label: "Name", key: "name" },
        { label: "Relationship", key: "relationship" },
        { label: "Date of Birth", key: "date_of_birth" },
        { label: "Gender", key: "gender" },
        { label: "Notes", key: "address" }
      ]
    }
  ],
  acknowledgement: [
    {
      title: "Employee Information (Master Data)",
      fields: [
        { label: "Employee Name", key: "employee_name" },
        { label: "Employee Code", key: "employee_code" },
        { label: "Department", key: "department_name" },
        { label: "Designation", key: "designation_name" },
        { label: "Location", key: "location_name" },
        { label: "Date of Joining", key: "date_of_joining" },
        { label: "Company", key: "company_name" },
        { label: "Business Unit", key: "business_unit" },
        { label: "Email", key: "work_email" },
        { label: "Mobile", key: "mobile" },
        { label: "Reporting Manager", key: "reporting_manager_name" },
        { label: "Employee Status", key: "employment_status" }
      ]
    },
    {
      title: "Acknowledgement Terms & Signature",
      fields: [
        { label: "Policy Manual Date", key: "manual_date" },
        { label: "Accepted Terms?", key: "accepted_terms" },
        { label: "Confirmation Date", key: "confirmation_date" }
      ]
    }
  ]
};

const StatusPill = ({ status }) => {
  const cls = ({
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    submitted: 'bg-amber-100 text-amber-800',
    draft: 'bg-slate-100 text-slate-700',
  })[status] || 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${cls}`}>{status}</span>;
};

export default function HRESSApprovals() {
  const [tab, setTab] = useState('change_requests');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');

  // Multi-Selection State
  const [selectedMap, setSelectedMap] = useState({});
  const [bulkModal, setBulkModal] = useState({ open: false, action: 'approve', remarks: '' });
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Decision modal
  const [dec, setDec] = useState({ open: false, id: '', remarks: '', kind: null, action: '' });
  const [saving, setSaving] = useState(false);

  // Preview state
  const [prevShow, setPrevShow] = useState(false);
  const [prevLoading, setPrevLoading] = useState(false);
  const [prevData, setPrevData] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [zoomUrl, setZoomUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = tab === 'change_requests'
        ? `${API_URL}/hr/profile-change-requests?status=${filter}`
        : `${API_URL}/hr/statutory-forms?status=${filter === 'pending' ? 'submitted' : filter}`;
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, tab]);

  useEffect(() => { load(); }, [tab, filter, load]);

  // Clear selections on tab or filter change
  useEffect(() => {
    setSelectedMap({});
  }, [tab, filter]);

  const filteredRows = rows.filter(r => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const empCode = (r.employee_code || '').toLowerCase();
    const empName = (r.employee_name || '').toLowerCase();
    const field = (r.field_path || '').toLowerCase();
    const formType = (r.form_type || '').toLowerCase();
    const reason = (r.reason || '').toLowerCase();
    return empCode.includes(q) || empName.includes(q) || field.includes(q) || formType.includes(q) || reason.includes(q);
  });

  const isRowSelectable = (r) => {
    if (tab === 'change_requests') return r.status === 'pending';
    return r.status === 'submitted';
  };

  const selectableRows = filteredRows.filter(isRowSelectable);
  const selectedCount = Object.keys(selectedMap).length;
  const isAllSelected = selectableRows.length > 0 && selectableRows.every(r => selectedMap[r.id]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedMap({});
    } else {
      const newMap = {};
      selectableRows.forEach(r => { newMap[r.id] = r; });
      setSelectedMap(newMap);
    }
  };

  const toggleSelectRow = (r) => {
    setSelectedMap(prev => {
      const next = { ...prev };
      if (next[r.id]) {
        delete next[r.id];
      } else {
        next[r.id] = r;
      }
      return next;
    });
  };

  const openDec = (id, kind, action) => setDec({ open: true, id, remarks: '', kind, action });

  const submitDec = async () => {
    setSaving(true);
    try {
      const endpoint = dec.kind === 'change_request'
        ? `${API_URL}/hr/profile-change-requests/${dec.id}/decision`
        : `${API_URL}/hr/statutory-forms/${dec.id}/decision`;
      await axios.post(endpoint, { action: dec.action, remarks: dec.remarks }, { withCredentials: true });
      toast.success(`Request ${dec.action}d`);
      setDec({ open: false, id: '', remarks: '', kind: null, action: '' });
      
      if (prevShow && prevData && prevData.id === dec.id) {
        setPrevShow(false);
      }
      
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async () => {
    const items = Object.values(selectedMap);
    if (!items.length) return;
    setBulkProcessing(true);
    let success = 0;
    let failed = 0;
    
    for (const item of items) {
      try {
        const kind = tab === 'change_requests' ? 'change_request' : 'statutory_form';
        const endpoint = kind === 'change_request'
          ? `${API_URL}/hr/profile-change-requests/${item.id}/decision`
          : `${API_URL}/hr/statutory-forms/${item.id}/decision`;
        await axios.post(endpoint, { action: bulkModal.action, remarks: bulkModal.remarks }, { withCredentials: true });
        success++;
      } catch (err) {
        failed++;
      }
    }
    
    setBulkProcessing(false);
    setBulkModal({ open: false, action: 'approve', remarks: '' });
    setSelectedMap({});
    toast.success(`Bulk Action Completed: ${success} processed successfully${failed > 0 ? `, ${failed} failed` : ''}`);
    load();
  };

  const downloadPdf = async (formId) => {
    try {
      const res = await axios.get(`${API_URL}/ess/statutory-forms/${formId}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `form_${formId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  const handleUnlockForm = async (formId) => {
    if (!window.confirm('Unlock this statutory form? It will revert to Draft and allow employee edits.')) return;
    try {
      await axios.post(`${API_URL}/hr/statutory-forms/${formId}/unlock`, {}, { withCredentials: true });
      toast.success('Form unlocked for employee editing');
      if (prevShow && prevData && prevData.id === formId) {
        setPrevShow(false);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlock form');
    }
  };

  const openPreview = async (formId) => {
    setPrevLoading(true);
    setPrevShow(true);
    setPrevData(null);
    setCollapsedSections({});
    try {
      const { data } = await axios.get(`${API_URL}/ess/statutory-forms/${formId}`, { withCredentials: true });
      setPrevData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview details');
      setPrevShow(false);
    } finally {
      setPrevLoading(false);
    }
  };

  const toggleSection = (title) => {
    setCollapsedSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const getAttachments = () => {
    if (!prevData) return [];
    const list = [];
    if (prevData.data?.photoUrl || prevData.employee_photo_url) {
      list.push({
        name: "Passport Size Photo",
        url: prevData.data?.photoUrl || prevData.employee_photo_url,
        type: "image"
      });
    }
    if (prevData.data?.signature) {
      list.push({
        name: "Employee Signature",
        url: prevData.data.signature,
        type: prevData.data.signature.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image'
      });
    }
    // Generic attachments from data values starting with http or api
    Object.entries(prevData.data || {}).forEach(([k, v]) => {
      if (typeof v === 'string' && (v.startsWith('http') || v.includes('/api/')) && k !== 'photoUrl' && k !== 'photoThumbnail') {
        const isPdf = v.toLowerCase().includes('.pdf') || k.toLowerCase().includes('pdf');
        list.push({
          name: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          url: v,
          type: isPdf ? 'pdf' : 'image'
        });
      }
    });
    return list;
  };

  const attachments = prevData ? getAttachments() : [];

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="hr-ess-approvals-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Employee Approvals</h1>
            <p className="text-sm text-slate-600 mt-1">Approve or reject employee-submitted profile changes and statutory forms.</p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search Code, Name, Field..."
                className="pl-9 pr-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 w-60"
              />
            </div>

            <select value={filter} onChange={e => setFilter(e.target.value)}
              data-testid="hr-approvals-status-filter"
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-medium text-slate-700">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        {/* Tabs & Bulk Action Bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 w-full max-w-lg">
            {[
              { key: 'change_requests', label: 'Profile Change Requests', icon: ClipboardList },
              { key: 'statutory_forms', label: 'Statutory Forms',          icon: KeyRound },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`hr-tab-${t.key}`}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center gap-2 justify-center ${
                  tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {selectedCount > 0 && (
            <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 px-4 py-1.5 rounded-lg text-sm animate-in fade-in duration-150">
              <span className="font-semibold text-indigo-900">{selectedCount} item{selectedCount > 1 ? 's' : ''} selected</span>
              <div className="h-4 w-px bg-indigo-200" />
              <button
                onClick={() => setBulkModal({ open: true, action: 'approve', remarks: '' })}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3 py-1.5 rounded-md flex items-center gap-1 shadow-sm transition"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Bulk Approve ({selectedCount})
              </button>
              <button
                onClick={() => setBulkModal({ open: true, action: 'reject', remarks: '' })}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xs px-3 py-1.5 rounded-md flex items-center gap-1 shadow-sm transition"
              >
                <XCircle className="w-3.5 h-3.5" /> Bulk Reject ({selectedCount})
              </button>
              <button
                onClick={() => setSelectedMap({})}
                className="text-xs text-slate-600 hover:text-slate-900 underline font-medium ml-1"
              >
                Deselect All
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center text-slate-500 py-14 text-sm">Nothing to review matching current criteria.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-100">
                {tab === 'change_requests' ? (
                  <tr>
                    <th className="w-10 px-3 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        disabled={selectableRows.length === 0}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5">Employee</th>
                    <th className="text-left px-4 py-2.5">Field</th>
                    <th className="text-left px-4 py-2.5">From → To</th>
                    <th className="text-left px-4 py-2.5">Reason</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5">Submitted</th>
                    <th className="text-right px-4 py-2.5">Actions</th>
                  </tr>
                ) : (
                  <tr>
                    <th className="w-10 px-3 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        disabled={selectableRows.length === 0}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5">Employee</th>
                    <th className="text-left px-4 py-2.5">Form Type</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5">Submitted</th>
                    <th className="text-right px-4 py-2.5">Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {filteredRows.map(r => {
                  const selectable = isRowSelectable(r);
                  const isChecked = !!selectedMap[r.id];

                  return (
                    <tr key={r.id} className={`border-b border-slate-100 ${isChecked ? 'bg-indigo-50/40' : 'hover:bg-slate-50/70'}`} data-testid={`approval-row-${r.id}`}>
                      <td className="w-10 px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={!selectable}
                          onChange={() => toggleSelectRow(r)}
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-30"
                        />
                      </td>
                      {tab === 'change_requests' ? (
                        <>
                          <td className="px-4 py-3 text-slate-900 font-medium">{r.employee_code}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.field_path}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">
                            <span className="text-slate-400">{r.current_value || '—'}</span>
                            <span className="mx-1 text-slate-400">→</span>
                            <span className="font-medium">{r.requested_value}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate" title={r.reason}>{r.reason || '—'}</td>
                          <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                          <td className="px-4 py-3 text-xs text-slate-500">{(r.submitted_at || '').slice(0, 10)}</td>
                          <td className="px-4 py-3 text-right">
                            {r.status === 'pending' && (
                              <div className="inline-flex gap-2">
                                <button data-testid={`approve-${r.id}`} onClick={() => openDec(r.id, 'change_request', 'approve')} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                </button>
                                <button data-testid={`reject-${r.id}`} onClick={() => openDec(r.id, 'change_request', 'reject')} className="text-red-700 hover:text-red-900 text-xs font-semibold flex items-center gap-1">
                                  <XCircle className="w-3.5 h-3.5" /> Reject
                                </button>
                              </div>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-slate-900 font-medium">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8 flex items-center justify-center overflow-hidden">
                                <AuthenticatedImage 
                                  src={r.employee_photo_url} 
                                  alt={r.employee_name} 
                                  className="h-full w-full object-cover rounded-full" 
                                  fallbackText={r.employee_name ? r.employee_name.split(' ').filter(Boolean).map(x => x[0]).join('').slice(0, 2).toUpperCase() : 'EMP'} 
                                />
                              </Avatar>
                              <div>
                                <div>{r.employee_name || r.employee_code}</div>
                                <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-700 uppercase">{r.form_type}</td>
                          <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                          <td className="px-4 py-3 text-xs text-slate-500">{(r.submitted_at || '').slice(0, 10)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex gap-3">
                              <button data-testid={`preview-${r.id}`} onClick={() => openPreview(r.id)} className="text-[#0052FF] hover:underline text-xs flex items-center gap-1">
                                <Eye className="w-3.5 h-3.5" /> Preview
                              </button>
                              <button data-testid={`download-${r.id}`} onClick={() => downloadPdf(r.id)} className="text-slate-600 hover:underline text-xs flex items-center gap-1">
                                <FileText className="w-3.5 h-3.5" /> PDF
                              </button>
                              {r.status === 'submitted' && (
                                <>
                                  <button data-testid={`approve-${r.id}`} onClick={() => openDec(r.id, 'statutory_form', 'approve')} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                  </button>
                                  <button data-testid={`reject-${r.id}`} onClick={() => openDec(r.id, 'statutory_form', 'reject')} className="text-red-700 hover:text-red-900 text-xs font-semibold flex items-center gap-1">
                                    <XCircle className="w-3.5 h-3.5" /> Reject
                                  </button>
                                </>
                              )}
                              {(r.status === 'approved' || r.status === 'submitted') && (
                                <button data-testid={`unlock-${r.id}`} onClick={() => handleUnlockForm(r.id)} className="text-amber-700 hover:text-amber-900 text-xs font-semibold flex items-center gap-1" title="Unlock for employee edit">
                                  <KeyRound className="w-3.5 h-3.5" /> Unlock
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Bulk Decision Confirmation Modal */}
      {bulkModal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-decision-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4 shadow-xl border border-slate-200 animate-in fade-in duration-150">
            <h3 className="text-lg font-semibold text-slate-900">
              Confirm Bulk {bulkModal.action === 'approve' ? 'Approval' : 'Rejection'}
            </h3>
            <p className="text-sm text-slate-600">
              You are about to <strong className={bulkModal.action === 'approve' ? 'text-emerald-700' : 'text-red-700'}>{bulkModal.action}</strong> {selectedCount} selected request{selectedCount > 1 ? 's' : ''}.
              This operation will process each request using its standard approval workflow rules.
            </p>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Remarks / Reason {bulkModal.action === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={bulkModal.remarks}
                onChange={e => setBulkModal(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder={bulkModal.action === 'approve' ? "Optional approval notes..." : "Reason for rejection..."}
                rows={3}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={() => setBulkModal({ open: false, action: 'approve', remarks: '' })}
                disabled={bulkProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkSubmit}
                disabled={bulkProcessing || (bulkModal.action === 'reject' && !bulkModal.remarks.trim())}
                className={bulkModal.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              >
                {bulkProcessing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirm Bulk {bulkModal.action === 'approve' ? 'Approval' : 'Rejection'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Full-Screen Overlay / Drawer */}
      {prevShow && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 md:p-6" data-testid="preview-modal">
          <div className="bg-slate-50 w-full h-full md:h-[95vh] md:max-w-6xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
            {prevLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-white space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#0052FF]" />
                <p className="text-sm text-slate-500 font-medium">Fetching submitted details…</p>
              </div>
            ) : !prevData ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-white p-6 text-center space-y-3">
                <XCircle className="w-10 h-10 text-red-500" />
                <p className="text-sm text-slate-600 font-medium">Failed to load preview.</p>
                <Button variant="outline" onClick={() => setPrevShow(false)}>Close</Button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="bg-white border-b border-slate-200 p-5 flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-bold text-slate-900">
                        {prevData.form_type === 'joining' ? 'Employee Details' : prevData.form_type === 'form_f' ? 'PF Nomination (EPF Form 2)' : prevData.form_type === 'gratuity' ? 'Gratuity Nomination (Form F)' : prevData.form_type === 'acknowledgement' ? 'Acknowledgement' : 'Health Insurance'}
                      </h2>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-blue-100 text-blue-800 tracking-wider">
                        REQ-{prevData.id.slice(-6).toUpperCase()}
                      </span>
                      <StatusPill status={prevData.status} />
                    </div>
                    <p className="text-xs text-slate-500 font-medium">
                      Submitted: {prevData.submitted_at ? new Date(prevData.submitted_at).toLocaleDateString('en-GB') : '—'}
                    </p>
                    <div className="flex gap-x-4 gap-y-1 flex-wrap text-xs text-slate-600 mt-2">
                      <div><span className="font-semibold text-slate-400">Employee:</span> {prevData.employee_name} ({prevData.employee_code})</div>
                      <div><span className="font-semibold text-slate-400">Department:</span> {prevData.department || '—'}</div>
                      <div><span className="font-semibold text-slate-400">Designation:</span> {prevData.designation || '—'}</div>
                      <div><span className="font-semibold text-slate-400">Company:</span> {prevData.company_name || '—'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Top Right Passport Photo */}
                    {(prevData.data?.photoUrl || prevData.employee_photo_url) && (
                      <div className="w-[100px] h-[120px] rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-slate-50 flex items-center justify-center shrink-0 cursor-pointer" onClick={() => setZoomUrl(prevData.data?.photoUrl || prevData.employee_photo_url)}>
                        <AuthenticatedImage 
                          src={prevData.data?.photoUrl || prevData.employee_photo_url} 
                          alt="Passport photo" 
                          className="w-full h-full object-cover hover:scale-105 transition-transform" 
                          fallbackText="No Photo Uploaded"
                        />
                      </div>
                    )}
                    <button onClick={() => setPrevShow(false)} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-md hover:bg-slate-100 self-start">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content columns */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 overflow-y-auto flex-1">
                  {/* Left panel: Data sections */}
                  <div className="lg:col-span-2 space-y-4">
                    {(PREVIEW_SCHEMAS[prevData.form_type] || []).map((sec, idx) => {
                      const isCollapsed = !!collapsedSections[sec.title];
                      const isRepeater = !!sec.repeater;
                      return (
                        <div key={idx} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                          <button
                            onClick={() => toggleSection(sec.title)}
                            className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-100 text-sm font-semibold text-slate-800 hover:bg-slate-100/70 transition-colors"
                          >
                            <span>{sec.title}</span>
                            {isCollapsed ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronUp className="w-4 h-4 text-slate-500" />}
                          </button>
                          {!isCollapsed && (
                            <div className="p-5">
                              {isRepeater ? (
                                <div className="space-y-4">
                                  {(prevData.data?.[sec.repeater] || []).map((row, rIdx) => (
                                    <div key={rIdx} className="bg-slate-50 border border-slate-200 rounded-lg p-4 relative">
                                      <span className="absolute top-3 right-3 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                                        {sec.repeater.slice(0, -1)} #{rIdx + 1}
                                      </span>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {sec.fields.map(f => (
                                          <div key={f.key}>
                                            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{f.label}</div>
                                            <div className="text-sm font-medium text-slate-800 mt-0.5">{row[f.key] || '—'}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                  {(prevData.data?.[sec.repeater] || []).length === 0 && (
                                    <p className="text-xs text-slate-400 italic">No nominees or dependents specified.</p>
                                  )}
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {sec.fields.map(f => (
                                    <div key={f.key}>
                                      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{f.label}</div>
                                      <div className="text-sm font-medium text-slate-800 mt-0.5">{prevData.data?.[f.key] || '—'}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Right panel: Attachments & Audit */}
                  <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
                      <h3 className="text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2">Attachments & Docs</h3>
                      <div className="space-y-3">
                        {attachments.map((att, aIdx) => (
                          <div key={aIdx} className="border border-slate-100 rounded-lg p-3 space-y-2 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-slate-700 truncate mr-2" title={att.name}>{att.name}</span>
                              <a 
                                href={att.url} 
                                download 
                                className="text-slate-400 hover:text-blue-600 transition-colors"
                                title="Download File"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            </div>
                            
                            {att.type === 'pdf' ? (
                              <div className="space-y-2">
                                <iframe src={att.url} className="w-full h-48 border border-slate-200 rounded bg-white" title={att.name} />
                                <a 
                                  href={att.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-[10px] text-blue-600 hover:underline font-semibold flex items-center gap-1"
                                >
                                  Open PDF in new tab <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              </div>
                            ) : (
                              <div className="relative group overflow-hidden border border-slate-200 rounded bg-white flex items-center justify-center h-28 cursor-pointer" onClick={() => setZoomUrl(att.url)}>
                                <AuthenticatedImage 
                                  src={att.url} 
                                  alt={att.name} 
                                  className="h-full w-full object-contain group-hover:scale-105 transition-transform" 
                                  fallbackText="No Photo Uploaded"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-semibold">
                                  Click to Zoom
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {attachments.length === 0 && (
                          <p className="text-xs text-slate-400 italic">No attachments submitted.</p>
                        )}
                      </div>
                    </div>

                    {/* Declaration & Timestamps */}
                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3 text-xs">
                      <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">Status Timeline</h3>
                      <div className="space-y-2.5">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Status</span>
                          <span className="font-semibold text-slate-800 capitalize">{prevData.status}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Submitted At</span>
                          <span className="font-medium text-slate-700">{prevData.submitted_at ? new Date(prevData.submitted_at).toLocaleString() : '—'}</span>
                        </div>
                        {prevData.decided_at && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Decided At</span>
                              <span className="font-medium text-slate-700">{new Date(prevData.decided_at).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Remarks</span>
                              <span className="font-medium text-slate-700 italic">{prevData.decision_remarks || '—'}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Controls */}
                <div className="bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 flex-wrap">
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-1.5"
                    onClick={() => downloadPdf(prevData.id)}
                  >
                    <Download className="w-4 h-4" /> Download PDF
                  </Button>
                  
                  {prevData.status === 'submitted' && (
                    <>
                      <Button 
                        onClick={() => openDec(prevData.id, 'statutory_form', 'approve')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </Button>
                      <Button 
                        onClick={() => openDec(prevData.id, 'statutory_form', 'reject')}
                        className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </Button>
                    </>
                  )}
                  
                  <Button variant="ghost" onClick={() => setPrevShow(false)}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-200" onClick={() => setZoomUrl(null)}>
          <div className="relative max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <AuthenticatedImage 
              src={zoomUrl} 
              alt="Zoomed view" 
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl" 
              fallbackText="Failed to load image"
            />
            <button onClick={() => setZoomUrl(null)} className="absolute -top-10 right-0 text-white bg-slate-800/80 hover:bg-slate-800 p-2 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {dec.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="decision-modal">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">
                {dec.action === 'approve' ? 'Approve' : 'Reject'} — {dec.kind === 'change_request' ? 'Change Request' : 'Statutory Form'}
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs font-medium text-slate-700">Remarks (optional for approve, required for reject)</label>
              <textarea rows={4} value={dec.remarks} onChange={e => setDec({ ...dec, remarks: e.target.value })}
                data-testid="decision-remarks"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setDec({ open: false, id: '', remarks: '', kind: null, action: '' })}>Cancel</Button>
              <Button onClick={submitDec} disabled={saving || (dec.action === 'reject' && !dec.remarks.trim())}
                data-testid="submit-decision"
                className={dec.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (dec.action === 'approve' ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />)}
                {dec.action === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
